/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

import type { SimpleObserver } from "@tedivo/tedivo-pure-helpers";

export interface IBaseHttpClient {
  readonly baseUrl: string;
  request<TResponse>(props: IRequestProps): Promise<IResponseModel<TResponse>>;
}

export abstract class BaseHttpClient {
  public readonly baseUrl: string;
  private readonly axiosInstance: AxiosInstance;

  private onOffLineObserver: SimpleObserver<{ isOnline: boolean }> | undefined =
    undefined;
  private setAuthorizationHeaders: (() => Record<string, string>) | undefined =
    undefined;
  private notifyXhrRequestFired: ((isAnonymous: boolean) => void) | undefined =
    undefined;
  private notifyError:
    | ((
        httpCode: number,
        code: "response" | "request" | "unknown",
        message: string,
      ) => void)
    | undefined = undefined;

  private baseHeaders: Record<string, string> = {};
  private readonly timeout: number;

  constructor(
    baseUrl: string,
    callbacks: IBaseHttpClientCallbacks,
    baseHeaders: Record<string, string> = {},
    timeout = 30000,
  ) {
    this.baseUrl = baseUrl.replace(/$^\//, "");
    this.axiosInstance = axios.create({ timeout });

    this.onOffLineObserver = callbacks.onOffLineObserver;
    this.setAuthorizationHeaders = callbacks.setAuthorizationHeaders;
    this.notifyXhrRequestFired = callbacks.notifyXhrRequestFired;
    this.notifyError = callbacks.notifyError;
    this.baseHeaders = baseHeaders;
    this.timeout = timeout;
  }

  public async request<TResponse>({
    method,
    url,
    data,
    isAnonymous = false,
  }: IRequestProps): Promise<IResponseModel<TResponse>> {
    if (this.onOffLineObserver?.getValue("isOnline") === false) {
      this.onOffLineObserver.notify();
      return {
        statusCode: 503,
        code: "general:onOffLine.offline",
        message: "The application is offline",
        data: undefined as never,
      };
    }

    try {
      const config: AxiosRequestConfig = {};
      config.headers = {
        ...this.baseHeaders,
      };

      if (this.setAuthorizationHeaders && !isAnonymous) {
        config.headers = {
          ...config.headers,
          ...this.setAuthorizationHeaders(),
        };
      }

      config.timeout = this.timeout;
      if (this.timeout > 30000) {
        config.signal = AbortSignal.timeout(this.timeout);
      }

      if (!isAnonymous && this.notifyXhrRequestFired)
        this.notifyXhrRequestFired(isAnonymous);

      const callUrl = `${this.baseUrl}/${url
        .replace(/\/{2,4}/g, "/")
        .replace(/^\//, "")}`;

      let response: AxiosResponse<TResponse> | undefined;
      let params: unknown | undefined = undefined;

      switch (method) {
        case "GET": {
          if (data) params = { id: data };
          response = await this.axiosInstance.get(callUrl, {
            ...config,
            params,
          });
          break;
        }
        case "POST": {
          response = await this.axiosInstance.post(callUrl, data, config);
          break;
        }
        case "PUT": {
          response = await this.axiosInstance.put(callUrl, data, config);
          break;
        }
        case "DELETE": {
          response = await this.axiosInstance.delete(callUrl, {
            ...config,
            params: data,
          });
          break;
        }
        default:
          break;
      }

      const statusCode = response?.status || 400;
      const responseModel: IResponseModel<TResponse> = {
        data: response?.data,
        statusCode,
      };

      return responseModel;
    } catch (error: unknown) {
      console.log("Error in network request", error);

      const errorResponse = error as IResponseError;

      if (errorResponse.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const httpCode = errorResponse.response.status || 400;
        const errRes = errorResponse.response.data;

        this.notifyError?.(
          httpCode,
          "response",
          httpCode === 401 || httpCode === 403
            ? errorResponse.response.data?.message || "errors:notAllowed"
            : error === undefined
            ? "errors:errorHasOcurred"
            : typeof errRes === "string"
            ? errRes
            : (errRes as any).translationKey ||
              (errRes as any).message ||
              errorResponse.response.message ||
              "errors:errorHasOcurred",
        );

        return {
          statusCode: errorResponse.response.status || 400,
          code: errorResponse.response.data?.code,
          message:
            errorResponse.response.data?.message ||
            errorResponse.response.data?.translationKey ||
            "Unknown error",
          data: undefined as never,
        };
      } else if (errorResponse.request) {
        // The request was made but no response was received
        // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
        // http.ClientRequest in node.js
        console.log("Error request");
        if (errorResponse.request !== 401)
          this.notifyError?.(
            400,
            "request",
            "The request was made but no response was received",
          );
      } else {
        // Something happened in setting up the request that triggered an Error
        console.log("Error");
        this.notifyError?.(
          400,
          "unknown",
          "Something happened in setting up the request that triggered an Error",
        );
      }

      return {
        statusCode: 400,
        code: "",
        message: "Unknown error",
        data: undefined as never,
      };
    }
  }
}

interface IRequestProps<T = unknown> {
  method: "GET" | "POST" | "DELETE" | "PUT";
  url: string;
  data?: TextDecodeOptions | FormData | T;
  isAnonymous?: boolean;
}

export type IResponseModel<T> =
  | {
      statusCode: number;
      data?: T;
      code?: never;
      message?: never;
    }
  | {
      statusCode: number;
      data: never;
      code?: string;
      message?: string;
    };

export type IResponseModelWithPagination<T> =
  | {
      statusCode: number;
      data?: TDataWithPagination<T>;
      code?: never;
      message?: never;
    }
  | {
      statusCode: number;
      data: never;
      code?: string;
      message?: string;
    };

export type TDataWithPagination<T> = {
  lastEvaluatedKey: string;
  data: T;
};

export type IResponseError = {
  response?: {
    status: number;
    data?: {
      code?: string;
      message?: string;
      translationKey?: string;
    };
    message?: string;
  };
  request?: unknown;
  message?: string;
};

export interface IBaseHttpClientCallbacks {
  onOffLineObserver?: SimpleObserver<{ isOnline: boolean }>;
  setAuthorizationHeaders?: () => Record<string, string>;
  notifyXhrRequestFired?: (isAnonymous: boolean) => void;
  notifyError?: (
    httpCode: number,
    code: "response" | "request" | "unknown",
    message: string,
  ) => void;
}

export function isHttpStatusCodeSuccess(statusCode: number): boolean {
  return statusCode >= 200 && statusCode < 300;
}
